import * as React from 'react';
import { ChevronDown, ChevronUp, Filter, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface CollapsibleFiltersProps {
  children: React.ReactNode;
  activeFilterCount: number;
  onClearFilters?: () => void;
  defaultExpanded?: boolean;
  className?: string;
  gridCols?: 1 | 2 | 3 | 4 | 5 | 6;
  /**
   * Controlled open state. When provided, the component renders **only the
   * fields panel** (no built-in header bar) — the trigger lives elsewhere
   * (`FilterToggleButton`, placed in the table's actions row). When omitted,
   * the legacy self-contained bar (with its own header + toggle) is used.
   */
  open?: boolean;
}

const GRID_COLS_CLASS: Record<NonNullable<CollapsibleFiltersProps['gridCols']>, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
};

export function CollapsibleFilters({
  children,
  activeFilterCount,
  onClearFilters,
  defaultExpanded = true,
  className,
  gridCols = 5,
  open,
}: CollapsibleFiltersProps) {
  const { t } = useTranslation();
  const [internalExpanded, setInternalExpanded] = React.useState(defaultExpanded);
  const isControlled = open !== undefined;

  // Controlled panel-only mode: just the fields grid (the trigger is external).
  if (isControlled) {
    if (!open) return null;
    return (
      <div className={cn('rounded-lg border border-[#e2e8f0] bg-white p-4', className)}>
        {activeFilterCount > 0 && onClearFilters && (
          <div className="mb-3 flex justify-end">
            <button
              type="button"
              onClick={onClearFilters}
              className="flex items-center gap-1 text-xs font-medium text-[#64748b] hover:text-[#1a2b4a]"
            >
              <X className="h-3 w-3" />
              {t('clearFilters')}
            </button>
          </div>
        )}
        <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2', GRID_COLS_CLASS[gridCols])}>
          {children}
        </div>
      </div>
    );
  }

  // Legacy self-contained mode (unchanged) — kept for callers not yet migrated.
  return (
    <div className={cn('bg-[#f8fafc] border-b border-[#e2e8f0]', className)}>
      <div
        className="flex items-center justify-between px-6 py-3 cursor-pointer hover:bg-[#f1f5f9] transition-colors"
        role="button"
        tabIndex={0}
        onClick={() => setInternalExpanded(!internalExpanded)}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setInternalExpanded(!internalExpanded)}
      >
        <div className="flex items-center gap-3">
          <Filter className="h-4 w-4 text-[#64748b]" />
          <span className="text-sm font-medium text-[#475569]">{t('actions.filter')}</span>
          {activeFilterCount > 0 && (
            <Badge
              variant="secondary"
              className="bg-[#1a2b4a] text-white text-xs px-2 py-0.5 rounded-full"
            >
              {activeFilterCount}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          {activeFilterCount > 0 && onClearFilters && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClearFilters();
              }}
              className="text-xs text-[#64748b] hover:text-[#1a2b4a] font-medium flex items-center gap-1"
            >
              <X className="h-3 w-3" />
              {t('clearFilters')}
            </button>
          )}
          {internalExpanded ? (
            <ChevronUp className="h-4 w-4 text-[#64748b]" />
          ) : (
            <ChevronDown className="h-4 w-4 text-[#64748b]" />
          )}
        </div>
      </div>

      {internalExpanded && (
        <div
          className={cn(
            'px-6 pb-4 pt-2 grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white border-t border-[#e2e8f0]',
            GRID_COLS_CLASS[gridCols]
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}

interface FilterToggleButtonProps {
  activeFilterCount: number;
  open: boolean;
  onToggle: () => void;
  className?: string;
}

/**
 * Compact "Filters" toggle for a table's actions row (sits next to the Columns
 * button). Drives a controlled `<CollapsibleFilters open={...}>` panel.
 */
export function FilterToggleButton({
  activeFilterCount,
  open,
  onToggle,
  className,
}: FilterToggleButtonProps) {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={onToggle}
      aria-expanded={open}
      className={cn(className)}
    >
      <Filter className="mr-2 h-4 w-4" />
      {t('actions.filter')}
      {activeFilterCount > 0 && (
        <Badge
          variant="secondary"
          className="ml-2 rounded-full bg-[#1a2b4a] px-1.5 py-0 text-xs text-white"
        >
          {activeFilterCount}
        </Badge>
      )}
      {open ? (
        <ChevronUp className="ml-2 h-4 w-4" />
      ) : (
        <ChevronDown className="ml-2 h-4 w-4" />
      )}
    </Button>
  );
}

interface FilterFieldProps {
  label: string;
  children: React.ReactNode;
  className?: string;
}

export function FilterField({ label, children, className }: FilterFieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
        {label}
      </label>
      {children}
    </div>
  );
}
